import { type ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  DataTableColumnHeader,
  Button,
  FieldHint,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui";
import {
  Save,
  X,
  Loader2,
  ClipboardCheck,
  EyeOff,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import type { Product, InventoryStatus } from "@/lib/types/inventory";
import { InventoryRowActions } from "./inventory-row-actions";
import {
  getScheduleNumeral,
  getStatusConfig,
  multiValueFilterFn,
} from "@/lib/utils/inventory-format";
import { NdcLink } from "@/components/ndc-link";
import { NotesCell } from "@/components/notes-cell";

export const createColumns = (
  onNDCClick?: (ndc: string) => void,
  onViewDetails?: (product: Product) => void,
  onEdit?: (item: Product) => void,
  editingRowId?: string | null,
  editingData?: {
    drug_name: string;
    drug_type?:
      | "schedule_ii"
      | "schedule_iii"
      | "schedule_iv"
      | "schedule_v"
      | null;
    reorder_point?: number;
  } | null,
  onFieldChange?: (
    field: "drug_name" | "drug_type" | "reorder_point",
    value: string
  ) => void,
  onSave?: (item: Product) => void,
  onCancel?: () => void,
  isSaving?: boolean,
  onAuditStock?: (itemId: string) => void,
  onViewActivity?: (item: Product) => void,
  onDelete?: (item: Product) => void,
  onEditNotes?: (item: Product) => void,
  onToggleHidden?: (item: Product) => void,
  readOnly?: boolean
): ColumnDef<Product>[] => [
  {
    accessorKey: "item_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Product" />
    ),
    cell: ({ row }) => {
      const isEditing = editingRowId === row.original.id;
      const itemName = row.getValue("item_name") as string;

      if (isEditing && editingData) {
        return (
          <div className="space-y-1.5">
            <Input
              key={`item-name-${row.original.id}`}
              defaultValue={editingData.drug_name}
              onChange={(e) => onFieldChange?.("drug_name", e.target.value)}
              placeholder="Drug name"
              className="h-8"
              disabled={isSaving}
            />
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Min. Stock Level:
              </span>
              <FieldHint>
                The reorder threshold for this drug. Once stock falls to or
                below this level, the item shows as needing reorder.
              </FieldHint>
              <Input
                key={`reorder-threshold-${row.original.id}`}
                type="number"
                min="0"
                defaultValue={
                  editingData.reorder_point ??
                  row.original.reorder_threshold ??
                  0
                }
                onChange={(e) =>
                  onFieldChange?.("reorder_point", e.target.value)
                }
                className="h-7 w-16 text-center text-xs"
                disabled={isSaving}
              />
            </div>
          </div>
        );
      }

      const packaging = row.original.packaging;
      const dosageForm = row.original.dosage_form;
      const labelerName = row.original.labeler_name;
      const isItemHidden = row.original.is_hidden;

      return (
        <div className={isItemHidden ? "opacity-60" : ""}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 cursor-pointer">
                  <span className="max-w-[400px] truncate text-sm text-foreground/90">
                    {itemName}
                  </span>
                  {isItemHidden && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      <EyeOff className="h-3 w-3" />
                      Hidden
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-md">
                <div className="space-y-1.5">
                  <p className="font-semibold">{itemName}</p>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    {dosageForm && <p>Form: {dosageForm}</p>}
                    {labelerName && <p>Manufacturer: {labelerName}</p>}
                    {packaging && packaging.length > 0 && (
                      <div>
                        <p className="font-medium mt-1">Packaging:</p>
                        <ul className="list-disc list-inside pl-1">
                          {packaging.map((pkg, idx) => (
                            <li key={idx}>{pkg}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
  {
    accessorKey: "ndc",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="NDC" />
    ),
    cell: ({ row }) => <NdcLink ndc={row.original.ndc} onClick={onNDCClick} />,
  },
  {
    accessorKey: "drug_type",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Schedule"
        className="justify-center"
      />
    ),
    cell: ({ row }) => {
      const type = row.getValue("drug_type") as string | null | undefined;
      const numeral = getScheduleNumeral(type);
      if (!numeral) {
        return (
          <div className="text-center">
            <span className="text-xs text-muted-foreground">—</span>
          </div>
        );
      }
      return (
        <div className="text-center">
          <Badge variant="outline" className="font-bold text-xs">
            {numeral}
          </Badge>
        </div>
      );
    },
    filterFn: multiValueFilterFn,
  },
  {
    accessorKey: "stock_calculated",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Stock"
        className="justify-center"
      />
    ),
    cell: ({ row }) => {
      const stock = row.getValue("stock_calculated") as number;
      const isEditing = editingRowId === row.original.id;

      if (isEditing) {
        return (
          <div className="flex items-center gap-2">
            <div className="text-center font-semibold min-w-[40px]">
              {typeof stock === "number" && !isNaN(stock) ? stock : 0}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAuditStock?.(row.original.id || "")}
              disabled={isSaving}
              className="h-7 text-xs"
            >
              <ClipboardCheck className="h-3 w-3 mr-1" />
              Audit
            </Button>
          </div>
        );
      }

      return (
        <div className="text-center font-semibold">
          {typeof stock === "number" && !isNaN(stock) ? stock : 0}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Status"
        className="justify-center"
      />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as InventoryStatus;
      const config = getStatusConfig(status);

      const iconProps = { className: "h-4 w-4", strokeWidth: 2 } as const;
      const icon =
        status === "in_stock" ? (
          <CheckCircle2 {...iconProps} className="h-4 w-4 text-secondary" />
        ) : status === "reorder_needed" ? (
          <AlertTriangle
            {...iconProps}
            className="h-4 w-4 text-text-secondary"
          />
        ) : (
          <XCircle {...iconProps} className="h-4 w-4 text-destructive" />
        );

      return (
        <div className="flex justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">{icon}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">{config.label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
    filterFn: multiValueFilterFn,
  },
  {
    id: "notes",
    header: () => <div className="text-center">Notes</div>,
    cell: ({ row }) => (
      <NotesCell
        hasNotes={(row.original.notes_count ?? 0) > 0}
        onClickNotes={() => onEditNotes?.(row.original)}
      />
    ),
    size: 60,
  },
  {
    id: "actions",
    header: () => null,
    cell: ({ row }) => {
      const isEditing = editingRowId === row.original.id;

      if (isEditing) {
        return (
          <div className="flex gap-1 justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onSave?.(row.original)}
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      }

      return (
        <InventoryRowActions
          ndc={row.original.ndc}
          itemName={row.original.item_name}
          inventoryId={row.original.id}
          isHidden={row.original.is_hidden}
          readOnly={readOnly}
          onViewDetails={() => onViewDetails?.(row.original)}
          onEdit={() => onEdit?.(row.original)}
          onViewActivity={
            onViewActivity ? () => onViewActivity(row.original) : undefined
          }
          onToggleHidden={() => onToggleHidden?.(row.original)}
          onDelete={() => onDelete?.(row.original)}
          onAudit={onAuditStock ? (id) => onAuditStock(id) : undefined}
        />
      );
    },
    size: 48,
  },
];
